/**
 * Make a half-streamed markdown string render cleanly.
 *
 * Streamed replies arrive one character at a time, so the raw syntax of any
 * construct the model is still typing is visible until its closing delimiter
 * lands — `Here is **the pol` shows literal asterisks, `[link](https://…` shows
 * the raw URL. This closes what can be closed (emphasis, code spans) and hides
 * what can't be rendered yet (incomplete links, images, tables), so text styles
 * itself as it appears instead of flickering through its markup.
 *
 * Meant for in-flight text only — the final, complete reply renders unmodified.
 */

const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/
const BARE_HEADING_RE = /^ {0,3}#{1,6}\s*$/
const BARE_LIST_MARKER_RE = /^ {0,3}([-*+]|\d+[.)])\s*$/
const TABLE_ROW_RE = /^ {0,3}\|/
const DELIMITER_CELL_RE = /^\s*:?-+:?\s*$/
const EMPHASIS_CHARS = new Set(['*', '_', '~'])

export function completePartialMarkdown(text: string): string {
  if (!text) return ''
  // An open fence renders as code all the way to the end of the document, so
  // there's nothing to close inside it — only the closing fence being typed,
  // which would otherwise show as backticks in the code block.
  const fence = openFence(text)
  if (fence) return dropPartialClosingFence(text, fence)
  return completeInlineTail(dropUnrenderableTrailingLines(text))
}

interface Fence {
  char: string
  length: number
}

function openFence(text: string): Fence | null {
  let open: Fence | null = null
  for (const line of text.split('\n')) {
    const match = FENCE_RE.exec(line)
    if (!match) continue
    const fence = { char: match[1][0], length: match[1].length }
    if (open === null) {
      open = fence
      continue
    }
    // A closing fence is the same character, at least as long as the opener,
    // and carries no info string. So a ``` line nested inside a ```` block is
    // code, not the end of it.
    const trailing = line.trimStart().slice(fence.length).trim()
    if (fence.char === open.char && fence.length >= open.length && trailing === '') open = null
  }
  return open
}

function dropPartialClosingFence(text: string, fence: Fence): string {
  const lines = text.split('\n')
  const last = lines[lines.length - 1]
  const trimmed = last.trimStart()
  // Too short to close the block yet — the run is still being typed.
  const isPartialFence =
    last.length - trimmed.length <= 3 &&
    trimmed.length > 0 &&
    trimmed.length < fence.length &&
    trimmed === fence.char.repeat(trimmed.length)
  if (!isPartialFence) return text
  return lines.slice(0, -1).join('\n') + '\n'
}

/**
 * Trim trailing lines whose markup can't render yet — a table header with no
 * delimiter row, a heading or bullet marker with no text. Each would otherwise
 * show as raw pipes/hashes until the rest of the line arrives.
 */
function dropUnrenderableTrailingLines(text: string): string {
  const lines = text.split('\n')
  let blockStart = lines.length - 1
  while (blockStart > 0 && lines[blockStart - 1].trim() !== '') blockStart--

  let dropFrom = lines.length

  const lastLine = lines[lines.length - 1]
  if (BARE_HEADING_RE.test(lastLine) || BARE_LIST_MARKER_RE.test(lastLine)) {
    dropFrom = lines.length - 1
  }

  const tableStart = lines.findIndex((line, i) => i >= blockStart && TABLE_ROW_RE.test(line))
  if (tableStart !== -1 && !hasRenderableDelimiterRow(lines, tableStart)) {
    dropFrom = Math.min(dropFrom, tableStart)
  }

  if (dropFrom >= lines.length) return text
  return lines.slice(0, dropFrom).join('\n') + (dropFrom > 0 ? '\n' : '')
}

/** GFM only renders a table once a delimiter row matches the header's cell count. */
function hasRenderableDelimiterRow(lines: string[], headerIndex: number): boolean {
  const headerCells = splitTableCells(lines[headerIndex]).length
  return lines.slice(headerIndex + 1).some((line) => {
    const cells = splitTableCells(line)
    return cells.length === headerCells && cells.every((cell) => DELIMITER_CELL_RE.test(cell))
  })
}

function splitTableCells(line: string): string[] {
  return line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|')
}

/**
 * Inline constructs can't span a blank line or a fenced block, so anything left
 * open must live in the trailing chunk — only that chunk needs rewriting.
 */
function completeInlineTail(text: string): string {
  let tailStart = 0
  const blankLine = /\n[ \t]*\n/g
  let match: RegExpExecArray | null
  while ((match = blankLine.exec(text)) !== null) tailStart = match.index + match[0].length

  let offset = 0
  for (const line of text.split('\n')) {
    offset += line.length + 1
    if (FENCE_RE.test(line)) tailStart = Math.max(tailStart, offset)
  }

  return text.slice(0, tailStart) + completeTail(text.slice(tailStart))
}

interface OpenDelimiter {
  marker: string
}

function completeTail(tail: string): string {
  const stack: OpenDelimiter[] = []
  let i = 0

  while (i < tail.length) {
    const char = tail[i]

    if (char === '\\') {
      i += 2
      continue
    }

    if (char === '`') {
      const run = runLength(tail, i, '`')
      const close = findRun(tail, i + run, '`', run)
      if (close !== -1) {
        i = close + run
        continue
      }
      // Nothing typed after the opener yet — drop it rather than render an
      // empty code box that immediately grows.
      if (i + run >= tail.length) return completeTail(tail.slice(0, i))
      stack.push({ marker: '`'.repeat(run) })
      return closeAll(tail, stack)
    }

    if (char === '[' || (char === '!' && tail[i + 1] === '[')) {
      const rewritten = rewriteIfIncompleteLink(tail, i)
      if (rewritten !== null) return completeTail(rewritten)
      i = skipCompleteLink(tail, i)
      continue
    }

    if (EMPHASIS_CHARS.has(char)) {
      const run = runLength(tail, i, char)
      const marker = char.repeat(run)
      const prev = charAt(tail, i - 1)
      const next = charAt(tail, i + run)

      if (stack.at(-1)?.marker === marker && canClose(char, prev, next)) {
        stack.pop()
        i += run
        continue
      }
      // A marker with nothing after it yet is the start of something — hide it
      // until it has content, otherwise the bare `**` shows for a frame.
      if (next === undefined) return completeTail(tail.slice(0, i))
      if (canOpen(char, prev, next)) stack.push({ marker })
      i += run
      continue
    }

    i++
  }

  return closeAll(tail, stack)
}

/**
 * While a link is incomplete, show just its label as plain text. Closing it
 * instead would produce a live link pointing at a truncated URL; hiding the
 * label would make the text jump once the destination lands.
 */
function rewriteIfIncompleteLink(tail: string, start: number): string | null {
  const isImage = tail[start] === '!'
  const labelStart = start + (isImage ? 2 : 1)
  // An incomplete image would render as a broken-image icon, so drop it whole.
  const keep = (label: string) => tail.slice(0, start) + (isImage ? '' : label)

  const labelEnd = findUnescaped(tail, labelStart, ']')
  if (labelEnd === -1) return keep(tail.slice(labelStart))

  const label = tail.slice(labelStart, labelEnd)
  if (tail[labelEnd + 1] === '(') {
    return findUnescaped(tail, labelEnd + 2, ')') === -1 ? keep(label) : null
  }
  // `[label]` with the destination still on its way. Anything further along the
  // line means it was a literal bracketed phrase, not a link.
  return labelEnd === tail.length - 1 ? keep(label) : null
}

function skipCompleteLink(tail: string, start: number): number {
  const labelEnd = findUnescaped(tail, start, ']')
  if (labelEnd === -1) return start + 1
  if (tail[labelEnd + 1] !== '(') return labelEnd + 1
  const destEnd = findUnescaped(tail, labelEnd + 2, ')')
  return destEnd === -1 ? labelEnd + 1 : destEnd + 1
}

function closeAll(tail: string, stack: OpenDelimiter[]): string {
  if (stack.length === 0) return tail
  // A closing delimiter can't be preceded by whitespace, so tuck the closers in
  // ahead of any trailing space the stream has already emitted.
  const body = tail.replace(/\s+$/, '')
  const closers = stack
    .map((entry) => entry.marker)
    .reverse()
    .join('')
  return body + closers + tail.slice(body.length)
}

function canOpen(char: string, prev: string | undefined, next: string | undefined): boolean {
  if (next === undefined || /\s/.test(next)) return false
  // Underscores inside a word never open emphasis (`snake_case_name`).
  if (char === '_' && prev !== undefined && /\w/.test(prev)) return false
  return true
}

function canClose(char: string, prev: string | undefined, next: string | undefined): boolean {
  if (prev === undefined || /\s/.test(prev)) return false
  if (char === '_' && next !== undefined && /\w/.test(next)) return false
  return true
}

/** Indexing a string past either end is `undefined` at runtime but not in the types. */
function charAt(text: string, index: number): string | undefined {
  return index >= 0 && index < text.length ? text[index] : undefined
}

function runLength(text: string, start: number, char: string): number {
  let length = 0
  while (text[start + length] === char) length++
  return length
}

/** Index where a run of exactly `length` `char`s starts, or -1. */
function findRun(text: string, from: number, char: string, length: number): number {
  let i = from
  while (i < text.length) {
    if (text[i] !== char) {
      i++
      continue
    }
    const run = runLength(text, i, char)
    if (run === length) return i
    i += run
  }
  return -1
}

function findUnescaped(text: string, from: number, char: string): number {
  for (let i = from; i < text.length; i++) {
    if (text[i] === '\\') {
      i++
      continue
    }
    if (text[i] === char) return i
  }
  return -1
}
